// ProductSearch.tsx - search box for the multifilter sidebar (matches trade).
// Captures a query and fires onSearch on Enter or clear; the parent sends it
// to the API as user_query.
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

interface ProductSearchProps {
  onSearch: (searchTerm: string) => void;
  isLoading?: boolean;
  initialValue?: string;
}

const ProductSearch: React.FC<ProductSearchProps> = ({
  onSearch,
  isLoading = false,
  initialValue = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchTerm(initialValue);
  }, [initialValue]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const executeSearch = () => {
    onSearch(searchTerm);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      executeSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    onSearch('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={0}
        sx={{
          py: 2,
          px: 0,
          mb: 0.5,
          borderTop: 'none',
          backgroundColor: 'background.paper',
          opacity: isLoading ? 0.85 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <SearchIcon sx={{ mr: 1, fontSize: '1.5rem' }} />
          <Typography fontWeight="bold" sx={{ fontSize: '1.2rem' }}>
            Search Products
          </Typography>
          {isLoading && (
            <Typography
              variant="caption"
              color="primary"
              sx={{ ml: 'auto', fontStyle: 'italic' }}
            >
              Searching...
            </Typography>
          )}
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by design name, SKU, or keyword..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          InputProps={{
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={handleClearSearch}
                  edge="end"
                  disabled={isLoading}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.default',
              '&:hover': { backgroundColor: 'background.paper' },
              '&.Mui-focused': { backgroundColor: 'background.paper' },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ProductSearch;
